import React, { useState, useContext } from "react";
import LoadingSuccessErrorAlert from "../Rules/Components/LoadingSuccessErrorAlert";
import { toast } from "Components/DesignSystem/Toast/useToast";
import Utils from "../../../Common/Utils";
import Button from "Components/DesignSystem/Button/Button";
import Checkbox from "Components/DesignSystem/Checkbox/Checkbox";
import { downloadPDW } from "api/Pdw";
import { checkJobStatus } from "api/Job";
import { AppContext } from "../../../Context/AppContext";
import { CategorySearch, CategoryGrid } from "./CategoryRadioPicker";

/**
 * "Download → Product Template" — pick one category, get a blank workbook for it.
 *
 * On the design system since § 126. Nothing about the job call, the 4s modal-close
 * timer or the 3s inline-alert clear moved.
 *
 * Three defects went with the migration:
 *
 * - The radios were half-controlled: `checked` with an `onClick` and no `onChange`,
 *   which React warns about on every render, and `value={selectedCategory.id}` so
 *   every radio in the list carried the *same* value.
 * - They shared `name="optradio"` with `ReversePDW`, which `/gms/pdw` renders at the
 *   same time — see `CategoryRadioPicker`'s docblock for what that did.
 * - The search box's magnifier was a real `<button>` with `pointerEvents: 'none'`:
 *   a Tab stop that does nothing. DS `Search` draws the icon as an icon.
 *
 * `instanceId` defaults here, unlike `ReversePDW` — only one copy of this component
 * is ever mounted at a time (the page dialog and the Products-list `TemplatePDW`
 * dialog are different screens), so there is no collision to design against.
 */
const PdwDownload = ({
  categoryTypeList,
  categoriesList,
  modalClose,
  instanceId = "pdw-pdwdownload",
}) => {
  const [selectedCategory, setselectedCategory] = useState({ id: null, name: "", });
  const [filteredList, setFilteredList] = useState({ ...categoriesList });
  /* The legacy input was UNCONTROLLED (no `value`); DS `Search` is controlled, so
     the text now lives in state. Same observable behaviour — the field already
     survived re-renders because nothing reset it. */
  const [searchString, setSearchString] = useState("");

  const [Loading, setLoading] = useState({ status: "", message: "", });
  const [downloadUrl, setDownloadUrl] = React.useState("");
  const [allowAttributes, setAllowAttributes] = useState(false)
  const appData = useContext(AppContext);
  const channelKey = appData?.channelKey;

  /*
    Headline plus the underlying detail, matching ReversePDW's banner (§ 127). The
    banner is the only success announcement now: legacy raised BOTH a toast and an
    inline alert for the same event, and the two did not even agree on the wording —
    "Downloaded successfully" in the corner, "Downloaded Successful" inline.
  */
  const statusBody = (headline, detail) => (
    <span className="tw-flex tw-flex-col">
      <span>{headline}</span>
      {detail ? (
        <span className="tw-text-ink-secondary" data-testid={`${instanceId}-status-detail`}>
          {detail}
        </span>
      ) : null}
    </span>
  );

  async function do_download_pdw() {
    setLoading({ status: "Loading", message: statusBody("Preparing your download\u2026") });
    try{
      if(!channelKey) return
      const data = await downloadPDW(selectedCategory.id, channelKey, allowAttributes)
      const res = await checkJobStatus(data?.jobid);

      if (res?.data) {
        setDownloadUrl(res.data);
        setselectedCategory({ id: null, name: null });
        display_error_message({
          status: "Success",
          message: statusBody("Your file is downloading"),
        });
      }
      else throw new Error("Download Failed");
    }
    catch(err){
      /* The error toast STAYS — unlike success, failure is not double-announced, and
         the toast carries the reason the banner summarises. */
      toast.error(err.message ?? "Unexpected Error");
      display_error_message({
        status: "Error",
        message: statusBody("Download failed", err.message),
      });
    }
    finally {
      setTimeout(() => {
        setDownloadUrl(undefined);
        modalClose();
      }, 4000)
    }
  }

  function display_error_message(data) {
    setLoading(data);
    setTimeout(() => {
      setLoading({ status: null, message: null });
    }, 3000);
  }

  async function handleSearchAttribute(value) {
    setSearchString(value);
    if (!Utils.isEmpty(value)) {
      let searchval = value.toString().trim().toLowerCase();
      let filteredData = []
        .concat(
          ...Object.values(categoriesList).map((categoryTypeItems) => {
            return categoryTypeItems.filter((item) => {
              return (
                (item.name != null &&
                  item.name.toLowerCase().includes(searchval)) ||
                (item.description != null &&
                  item.description.toLowerCase().includes(searchval))
              );
            });
          })
        )
        .reduce((acc, category) => {
          (acc[category.cattype] = acc[category.cattype] || []).push(category);
          return acc;
        }, {});
      setFilteredList(filteredData);
    } else {
      setFilteredList(categoriesList);
    }
  }

  return (
    <div data-testid={instanceId}>
      <CategorySearch
        instanceId={instanceId}
        value={searchString}
        onChange={handleSearchAttribute}
        label="Search categories for a template"
        className="tw-max-w-[380px] tw-mb-5"
      />

      <CategoryGrid
        instanceId={instanceId}
        categoryTypeList={categoryTypeList}
        filteredList={filteredList}
        selectedCategoryId={selectedCategory.id}
        onSelect={(item) => setselectedCategory({ id: item.id, name: item.name })}
      />

      <div className="tw-mt-6 tw-bg-surface-sunken tw-rounded-ctl tw-w-max tw-px-3 tw-py-2">
        <Checkbox
          checked={allowAttributes}
          onChange={(checked) => setAllowAttributes(checked)}
          label="Add attribute values to the template"
          data-testid={`${instanceId}-add-attributes`}
        />
      </div>

      {/*
        Download left, Cancel beside it — the order this screen shipped with. The
        Data Model tab's far-left-Cancel rule (§ 122) applies to a Dialog FOOTER;
        this pair sits inside the scrolling content, so moving Cancel to the far
        left of a full-width row would strand it under the category grid.
      */}
      <div className="tw-mt-6 tw-flex tw-items-center tw-gap-3">
        <Button
          variant="primary"
          id={`${instanceId}-download-btn`}
          data-testid={`${instanceId}-download-btn`}
          /* Also disabled while the job runs. It gated on the category alone, so a
             second click during the poll started a second download job. */
          disabled={selectedCategory.id === null || Loading.status === "Loading"}
          onClick={() => do_download_pdw(channelKey)}
        >
          Download as XLS
        </Button>
        <Button
          variant="secondary"
          id={`${instanceId}-cancel-btn`}
          data-testid={`${instanceId}-cancel-btn`}
          onClick={modalClose}
        >
          Cancel
        </Button>
      </div>

      <div className="tw-mt-4">
        <LoadingSuccessErrorAlert loading={Loading} />
      </div>

      {/* Scoped id — `iframe_pdw_download` was hardcoded in four components. */}
      <iframe
        style={{ display: "none" }}
        src={downloadUrl}
        id={`${instanceId}-iframe`}
        title="pdw_download_create"
      />
    </div>
  );
};
export default PdwDownload;
